import { useState, useEffect } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  Users,
  Mail,
  MessageSquare,
  Newspaper,
  Package,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  User,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Shield,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import ThemeToggle from "../../components/common/ThemeToggle";
import { adminLogout, refreshToken } from "../../store/slices/authSlice";
import { notificationService } from "../../api/services/notificationService";
import { useToast } from "../../components/common/ThemeToaster";
import { AdminRole } from "../../entities";

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { success: toastSuccess, error: toastError } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingStats, setLoadingStats] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  // Apply dark mode class
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
    setIsUserDropdownOpen(false);
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  const { admin, isAdmin, isAuthenticated } = useSelector(
    (state) => state.auth,
  );

  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(window.atob(base64));
      const expirationTime = payload.exp * 1000;
      return Date.now() >= expirationTime;
    } catch (error) {
      console.error("Error decoding token:", error);
      return true;
    }
  };

  const attemptTokenRefresh = async () => {
    if (isRefreshing) return false;
    setIsRefreshing(true);
    const refreshTokenValue = localStorage.getItem("refreshToken");
    if (!refreshTokenValue) {
      setIsRefreshing(false);
      return false;
    }
    try {
      await dispatch(refreshToken(refreshTokenValue)).unwrap();
      return true;
    } catch (error) {
      return false;
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getAdminUnreadCount();
      const count = response?.data?.unreadCount || response?.unreadCount || 0;
      setUnreadCount(count);
    } catch (err) {
      console.error("Error fetching unread count:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  const getSidebarItems = () => {
    const role = admin?.role;
    const allItems = [
      {
        path: "/admin",
        icon: LayoutDashboard,
        label: "Dashboard",
        badge: null,
        end: true,
        roles: [AdminRole.SUPER_ADMIN, AdminRole.ADMIN, AdminRole.EDITOR],
      },
      {
        path: "/admin/products",
        icon: Package,
        label: "Products",
        badge: null,
        roles: [AdminRole.SUPER_ADMIN, AdminRole.ADMIN],
      },
      {
        path: "/admin/requests",
        icon: Mail,
        label: "Requests",
        badge: null,
        roles: [AdminRole.SUPER_ADMIN, AdminRole.ADMIN, AdminRole.EDITOR],
      },
      {
        path: "/admin/messages",
        icon: MessageSquare,
        label: "Messages",
        badge: null,
        roles: [AdminRole.SUPER_ADMIN, AdminRole.ADMIN, AdminRole.EDITOR],
      },
      {
        path: "/admin/notifications",
        icon: Bell,
        label: "Notifications",
        badge: unreadCount > 0 ? unreadCount : null,
        roles: [AdminRole.SUPER_ADMIN, AdminRole.ADMIN, AdminRole.EDITOR],
      },
      {
        path: "/admin/blog",
        icon: Newspaper,
        label: "Blog",
        badge: null,
        roles: [AdminRole.SUPER_ADMIN, AdminRole.ADMIN, AdminRole.EDITOR],
      },
      {
        path: "/admin/admins",
        icon: Users,
        label: "Admins",
        badge: null,
        roles: [AdminRole.SUPER_ADMIN],
      },
      {
        path: "/admin/settings",
        icon: Settings,
        label: "Settings",
        badge: null,
        roles: [AdminRole.SUPER_ADMIN, AdminRole.ADMIN],
      },
    ];
    return allItems.filter((item) => item.roles.includes(role));
  };

  const sidebarItems = getSidebarItems();

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated || !isAdmin || !admin) {
        navigate("/admin/login", { replace: true });
        return;
      }
      const token = localStorage.getItem("accessToken");
      if (!token) {
        handleLogout();
        return;
      }
      if (isTokenExpired(token)) {
        const refreshed = await attemptTokenRefresh();
        if (!refreshed) {
          handleLogout();
          return;
        }
      }
    };
    checkAuth();
    fetchUnreadCount();
    const intervalId = setInterval(
      () => {
        const token = localStorage.getItem("accessToken");
        if (token && isTokenExpired(token)) {
          attemptTokenRefresh();
        }
        fetchUnreadCount();
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(intervalId);
  }, [navigate, dispatch, isAuthenticated, isAdmin, admin]);

  const handleLogout = () => {
    dispatch(adminLogout());
    navigate("/admin/login", { replace: true });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case AdminRole.SUPER_ADMIN:
        return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300";
      case AdminRole.ADMIN:
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
      case AdminRole.EDITOR:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case AdminRole.SUPER_ADMIN:
        return ShieldAlert;
      case AdminRole.ADMIN:
        return ShieldCheck;
      default:
        return Shield;
    }
  };

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full blur-xl opacity-50 animate-pulse" />
            <div className="relative w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  const RoleIcon = getRoleIcon(admin.role);
  const roleBadgeColor = getRoleBadgeColor(admin.role);
  const roleDisplayName = admin.role?.replace("_", " ") || "Administrator";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      {/* Desktop Sidebar */}
      <motion.aside
        initial={{ width: isSidebarOpen ? 280 : 80 }}
        animate={{ width: isSidebarOpen ? 280 : 80 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="hidden lg:block fixed left-0 top-0 h-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-800/50 shadow-xl z-30"
      >
        <div className="relative h-full flex flex-col">
          {/* Logo Area */}
          <div className="h-20 flex items-center justify-between px-4 border-b border-gray-200/50 dark:border-gray-800/50">
            <AnimatePresence mode="wait">
              {isSidebarOpen ? (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-md">
                    <img
                      src="/Icon.png"
                      alt="Logo"
                      className="w-5 h-5 object-contain brightness-0 invert"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-lg bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                      TradeBridge
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 tracking-wide -mt-0.5">
                      Admin Portal
                    </span>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="w-full flex justify-center"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-md">
                    <img
                      src="/Icon.png"
                      alt="Logo"
                      className="w-5 h-5 object-contain brightness-0 invert"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
            >
              {isSidebarOpen ? (
                <ChevronLeft size={18} />
              ) : (
                <ChevronRight size={18} />
              )}
            </button>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 overflow-y-auto py-6 px-3">
            <ul className="space-y-2">
              {sidebarItems.map((item) => {
                const isActive = item.end
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path);
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`}
                    >
                      <Icon size={20} className="flex-shrink-0" />
                      {isSidebarOpen && (
                        <span className="flex-1 text-sm font-medium whitespace-nowrap overflow-hidden">
                          {item.label}
                        </span>
                      )}
                      {item.badge && isSidebarOpen && (
                        <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}
                      {isActive && isSidebarOpen && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Admin Profile Section */}
          <div className="border-t border-gray-200/50 dark:border-gray-800/50 p-4">
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="flex items-center gap-3 w-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white flex-shrink-0 shadow-md">
                <span className="text-sm font-bold">
                  {admin?.firstName?.charAt(0)}
                  {admin?.lastName?.charAt(0)}
                </span>
              </div>
              {isSidebarOpen && (
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {admin?.firstName} {admin?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {admin?.email}
                  </p>
                </div>
              )}
              {isSidebarOpen && (
                <ChevronDown
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    isProfileMenuOpen ? "rotate-180" : ""
                  }`}
                />
              )}
            </button>

            <AnimatePresence>
              {isProfileMenuOpen && isSidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-2 py-2 bg-white/90 dark:bg-gray-900/90 rounded-xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg"
                >
                  <Link
                    to="/admin/settings"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    <User size={16} />
                    Profile Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileSidebarOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed left-0 top-0 h-full w-80 bg-white dark:bg-gray-900 shadow-2xl z-50 lg:hidden flex flex-col"
            >
              {/* Mobile Logo */}
              <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-md">
                    <img
                      src="/Icon.png"
                      alt="Logo"
                      className="w-5 h-5 object-contain brightness-0 invert"
                    />
                  </div>
                  <div>
                    <span className="font-bold text-lg text-gray-900 dark:text-white">
                      TradeBridge
                    </span>
                    <p className="text-[10px] text-gray-500 -mt-0.5">
                      Admin Portal
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 overflow-y-auto py-4 px-3">
                <ul className="space-y-1">
                  {sidebarItems.map((item) => {
                    const isActive = item.end
                      ? location.pathname === item.path
                      : location.pathname.startsWith(item.path);
                    const Icon = item.icon;
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setIsMobileSidebarOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                            isActive
                              ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                        >
                          <Icon size={20} className="flex-shrink-0" />
                          <span className="flex-1 text-sm font-medium">
                            {item.label}
                          </span>
                          {item.badge && (
                            <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                              {item.badge > 99 ? "99+" : item.badge}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              {/* Mobile Profile */}
              <div className="border-t border-gray-200 dark:border-gray-800 p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white">
                    <span className="text-sm font-bold">
                      {admin?.firstName?.charAt(0)}
                      {admin?.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {admin?.firstName} {admin?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {admin?.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-medium hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${
          isSidebarOpen ? "lg:ml-[280px]" : "lg:ml-[80px]"
        } ml-0`}
      >
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-800/50">
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden p-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
            >
              <Menu size={20} />
            </button>

            {/* Search Bar - Hidden on very small screens */}
            <div className="hidden sm:block flex-1 max-w-md">
              <div className="relative">
                <Search
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-12 pr-4 py-2.5 bg-gray-100/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-2 sm:gap-3 ml-auto">
              {/* Theme Toggle */}
              <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />

              {/* User Dropdown Menu */}
              <div className="relative">
                <button
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center gap-2 p-1.5 sm:p-2 rounded-xl bg-gray-100/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md">
                    <span className="text-xs font-bold text-white">
                      {admin?.firstName?.charAt(0)}
                      {admin?.lastName?.charAt(0)}
                    </span>
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {admin?.firstName}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-gray-500 transition-transform duration-200 ${
                      isUserDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {isUserDropdownOpen && (
                    <>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsUserDropdownOpen(false)}
                        className="fixed inset-0 z-40"
                      />
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute right-0 mt-2 w-64 sm:w-72 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg overflow-hidden z-50"
                      >
                        <div className="p-4 bg-gradient-to-r from-primary-50/50 to-secondary-50/50 dark:from-primary-900/20 dark:to-secondary-900/20 border-b border-gray-100 dark:border-gray-800">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md">
                              <span className="text-sm font-bold text-white">
                                {admin?.firstName?.charAt(0)}
                                {admin?.lastName?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {admin?.firstName} {admin?.lastName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {admin?.email}
                              </p>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center gap-2">
                            <RoleIcon size={12} className="text-primary-500" />
                            <span
                              className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${roleBadgeColor}`}
                            >
                              {roleDisplayName}
                            </span>
                          </div>
                        </div>

                        <div className="p-2">
                          <Link
                            to="/admin/settings"
                            onClick={() => setIsUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          >
                            <User size={16} />
                            Profile Settings
                          </Link>
                          <Link
                            to="/admin/notifications"
                            onClick={() => setIsUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          >
                            <Bell size={16} />
                            Notifications
                            {unreadCount > 0 && (
                              <span className="ml-auto px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                                {unreadCount}
                              </span>
                            )}
                          </Link>
                          <div className="border-t border-gray-100 dark:border-gray-800 my-1"></div>
                          <button
                            onClick={() => {
                              setIsUserDropdownOpen(false);
                              handleLogout();
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors mt-1"
                          >
                            <LogOut size={16} />
                            Logout
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
